import { useParams } from "#routers/hooks";
import { DeleteWorkerDialog } from "#src/batteries-included-components/Dialogs/DeleteWorkerDialog";
import WorkerRunsTablePanel from "#src/batteries-included-components/Panels/TablePanels/WorkerRunsTablePanel/WorkerRunsTablePanel";
import {
  useGetOneWorker,
  useGetOneWorkerVersion,
  useListWorkerVersions,
  useUpdateOneWorkerVersion,
} from "#src/components/hooks/adapters/useWorkers";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
  useIsFeatureEnabled,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useRunWorkerDrawer } from "#src/contexts/RunWorkerDrawer";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useNavigate, useSearchParams } from "#src/Routers/hooks";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { WorkerVersionStatusToPillVariantMapping } from "#src/view-models/workers";
import {
  type DropdownMenuOption,
  type PillPopoverOptionType,
  Button,
  DropdownMenu,
  EmptyState,
  Icon,
  Page,
  Panel,
  PillPopover,
  RichTextMarkdownDataDisplay,
  Tab,
} from "@validereinc/common-components";
import {
  SortDirection,
  WorkerAdapter,
  WorkerType,
  WorkerVersionStatus,
} from "@validereinc/domain";
import {
  FeatureFlags,
  PermissionsMap,
  getValidWorkerVersionStatusTransition,
  type WorkerVersionStatusTransitionMapType,
} from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useEffect, useMemo, useState } from "react";
import { WorkerDetailRoutePath } from ".";
import { WorkerVersionsRoutePath } from "../..";
import { WorkerVersionsCreateRoutePath } from "../versions/create";
import { WorkerDetailPreviousVersionBanner } from "./WorkerDetailPreviousVersionBanner";
import { WorkerDetailTimelinePanelDrawer } from "./WorkerDetailTimelinePanelDrawer";
import { WorkerDetailTimelinePanelTrigger } from "./WorkerDetailTimelinePanelTrigger";
import { WorkerVersionSchemaPanels } from "./WorkerVersionSchemaPanels";

const WorkerDetailPageTabs = {
  Overview: "overview",
  Runs: "runs",
} as const;

const DEFAULT_TAB_KEY = WorkerDetailPageTabs.Overview;
const WORKER_VERSIONS_DRAWER_KEY = "worker-versions";

export const WorkerDetailPage = () => {
  const { workerId } = useParams<{ workerId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    v2: {
      userInfo: { checkHasPermissions },
    },
  } = useAuthenticatedContext();
  const canDeleteWorker = checkHasPermissions(PermissionsMap.WorkerDelete);
  const [hasWorkerRunWritePermission] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.CustomCodeFeatureFlag,
    permissionQuery: PermissionsMap.WorkerRunWrite,
  });
  const [hasWorkerWritePermission] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.CustomCodeFeatureFlag,
    permissionQuery: PermissionsMap.WorkerWrite,
  });
  const [isDevBetaEnabled] = useIsFeatureEnabled(FeatureFlags.DevBeta);
  const [canViewWorkerRuns] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.CustomCodeFeatureFlag,
    permissionQuery: {
      $and: [PermissionsMap.WorkerRead, PermissionsMap.WorkerRunRead],
    },
  });

  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      workerId ? `worker-detail-runs-${workerId}` : "worker-detail-runs"
    );

  const [workerDeleteDialogState, setWorkerDeleteDialogState] = useState<
    WorkerType | undefined
  >();
  const updateVersionMutation = useUpdateOneWorkerVersion();
  const { setSelectedWorkerId, setIsOpen: setRunWorkerDrawerIsOpen } =
    useRunWorkerDrawer();

  const { data: workerData, isLoading: isLoadingWorker } = useGetOneWorker(
    { id: workerId },
    { enabled: Boolean(workerId) }
  );
  const worker = workerData?.data;
  const listVersionsParams = useMemo(
    () =>
      workerId
        ? {
            meta: { id: workerId },
            page: 1,
            pageSize: 500,
            sortBy: "version" as const,
            sortDirection: SortDirection.DESCENDING,
            filters: {},
          }
        : null,
    [workerId]
  );
  const { data: versionsData, isLoading: isLoadingVersions } =
    useListWorkerVersions(
      listVersionsParams as Parameters<
        typeof WorkerAdapter.versions.getList
      >[0],
      { enabled: Boolean(workerId) }
    );
  const versions = versionsData?.data ?? [];
  const latestVersion = versions[0];
  const hasActiveVersion = versions.some(
    (version) => version.status === WorkerVersionStatus.ACTIVE
  );

  const [breadcrumbs] = useBreadcrumbsFromRoute(WorkerDetailRoutePath, {
    "/detail": {
      title: worker ? `${worker.name}` : WorkerDetailRoutePath.title,
    },
  });

  const createInfo = useActionDetails({
    type: "create",
    date: worker?.created_at,
    byUserId: worker?.created_by,
  });
  const updateInfo = useActionDetails({
    type: "update",
    date: worker?.updated_at,
    byUserId: worker?.updated_by,
  });
  const metaSegments = [updateInfo.description, createInfo.description].filter(
    (s) => s !== undefined
  );

  const rawVersionFromSearch = Array.isArray(searchParams?.version)
    ? searchParams.version[0]
    : searchParams?.version;
  const selectedVersionFromSearch =
    rawVersionFromSearch != null && rawVersionFromSearch !== ""
      ? Number(rawVersionFromSearch)
      : undefined;
  const selectedVersion = useMemo(() => {
    if (
      selectedVersionFromSearch != null &&
      !Number.isNaN(selectedVersionFromSearch) &&
      versions.some((version) => version.version === selectedVersionFromSearch)
    ) {
      return selectedVersionFromSearch;
    }
    return versions[0]?.version;
  }, [selectedVersionFromSearch, versions]);

  const { data: selectedVersionData, isLoading: isLoadingSelectedVersion } =
    useGetOneWorkerVersion(
      {
        workerId: workerId ?? "",
        version: selectedVersion ?? 0,
      },
      {
        enabled:
          Boolean(workerId) &&
          selectedVersion != null &&
          !Number.isNaN(selectedVersion),
      }
    );

  useEffect(() => {
    if (isLoadingVersions) {
      return;
    }

    if (versions.length === 0) {
      if (rawVersionFromSearch != null && rawVersionFromSearch !== "") {
        const { version: _version, ...restQuery } = searchParams;
        setSearchParams(restQuery);
      }
      return;
    }

    if (selectedVersion == null) return;
    const selectedVersionAsString = String(selectedVersion);
    if (rawVersionFromSearch === selectedVersionAsString) return;

    setSearchParams({
      ...searchParams,
      version: selectedVersionAsString,
    });
  }, [
    versions,
    isLoadingVersions,
    selectedVersion,
    rawVersionFromSearch,
    searchParams,
    setSearchParams,
  ]);

  const isLoading =
    Boolean(workerId) &&
    (isLoadingWorker ||
      isLoadingVersions ||
      (selectedVersion != null && isLoadingSelectedVersion));

  const tabFromSearch = searchParams?.tab ?? DEFAULT_TAB_KEY;
  const activeTabKey =
    tabFromSearch !== WorkerDetailPageTabs.Overview &&
    tabFromSearch !== WorkerDetailPageTabs.Runs
      ? DEFAULT_TAB_KEY
      : !canViewWorkerRuns && tabFromSearch === WorkerDetailPageTabs.Runs
        ? DEFAULT_TAB_KEY
        : tabFromSearch;
  const isOverviewTabActive = activeTabKey === WorkerDetailPageTabs.Overview;
  const handleActiveTabKeyChange = (tabKey: string) => {
    setSearchParams({ ...searchParams, tab: tabKey });
  };

  const shouldShowCreateNewVersionAction =
    isDevBetaEnabled &&
    hasWorkerWritePermission &&
    hasActiveVersion &&
    workerId != null;
  const showNoActiveVersionEmptyState = !hasActiveVersion && !isLoadingVersions;

  const description = selectedVersionData?.description?.trim();
  const noVersionEmptyStateTitle = showNoActiveVersionEmptyState
    ? "No active version available."
    : selectedVersion != null
      ? "Version not found."
      : "No versions available.";
  const noVersionEmptyStateSuggestion = showNoActiveVersionEmptyState
    ? "Create a worker version and deploy it to get started."
    : undefined;
  const canCreateFirstVersion =
    showNoActiveVersionEmptyState &&
    isDevBetaEnabled &&
    hasWorkerWritePermission &&
    workerId != null;
  const isViewingOldVersion =
    selectedVersionData != null &&
    latestVersion != null &&
    selectedVersionData.version !== latestVersion.version;
  const workerVersionsDrawerKey = workerId
    ? `${WORKER_VERSIONS_DRAWER_KEY}-${workerId}`
    : WORKER_VERSIONS_DRAWER_KEY;
  const nextStatusForSelectedVersion = selectedVersionData
    ? getValidWorkerVersionStatusTransition(selectedVersionData.status)
    : undefined;
  const workerVersionStatusPillOptions = useMemo<
    Array<PillPopoverOptionType<WorkerVersionStatusTransitionMapType>>
  >(() => {
    if (selectedVersionData == null) {
      return [];
    }

    const statuses = nextStatusForSelectedVersion
      ? [selectedVersionData.status, nextStatusForSelectedVersion]
      : [selectedVersionData.status];

    return statuses.map((status) => ({
      label: toTitleCaseString(status, { intent: "ui" }),
      value: status,
      variant: WorkerVersionStatusToPillVariantMapping[status] ?? "default",
    }));
  }, [selectedVersionData, nextStatusForSelectedVersion]);
  const canEditSelectedVersionStatus =
    hasWorkerWritePermission &&
    workerId != null &&
    selectedVersionData != null &&
    nextStatusForSelectedVersion != null;

  const workerActionMenuOptions: DropdownMenuOption[] = [
    ...(shouldShowCreateNewVersionAction
      ? [
          {
            label: "Create Worker Version",
            onClick: () =>
              navigate({
                pathname: WorkerVersionsCreateRoutePath.toLink({
                  pathParams: { workerId },
                }),
              }),
            slotLeft: ({ iconProps }) => (
              <Icon
                {...iconProps}
                variant="plus-circle"
              />
            ),
          },
        ]
      : []),
    ...(canDeleteWorker && worker != null
      ? [
          {
            label: "Delete Worker",
            variant: "danger" as const,
            slotLeft: ({ iconProps }) => (
              <Icon
                {...iconProps}
                variant="trash"
              />
            ),
            onClick: () => setWorkerDeleteDialogState(worker),
          },
        ]
      : []),
  ];

  return (
    <>
      <DeleteWorkerDialog
        worker={workerDeleteDialogState}
        onClose={() => setWorkerDeleteDialogState(undefined)}
        onDeleted={() =>
          navigate({ pathname: WorkerVersionsRoutePath.toLink({}) })
        }
      />
      <Page
        title={
          worker ? (
            <>
              {worker.name}&nbsp;
              {selectedVersionData != null ? (
                <PillPopover
                  options={workerVersionStatusPillOptions}
                  value={selectedVersionData.status}
                  isEditable={canEditSelectedVersionStatus}
                  isBusy={updateVersionMutation.isLoading}
                  onChange={(newStatus) => {
                    if (workerId == null || selectedVersionData == null) return;
                    if (newStatus === selectedVersionData.status) return;
                    if (newStatus !== nextStatusForSelectedVersion) {
                      return;
                    }
                    updateVersionMutation.mutate({
                      id: workerId,
                      data: { status: newStatus },
                      meta: { version: selectedVersionData.version },
                    });
                  }}
                />
              ) : null}
            </>
          ) : (
            WorkerDetailRoutePath.title
          )
        }
        category={WorkerDetailRoutePath.subtitle}
        breadcrumbs={breadcrumbs}
        isLoading={isLoading}
        activeTabKey={activeTabKey}
        onActiveTabKeyChange={handleActiveTabKeyChange}
        renderMeta={
          metaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => <MetaSegments values={metaSegments} />
        }
        rightSlot={
          workerId != null && isOverviewTabActive ? (
            <WorkerDetailTimelinePanelDrawer
              drawerKey={workerVersionsDrawerKey}
              isLoading={isLoadingVersions}
              versions={versions}
              selectedVersion={selectedVersion}
              latestVersionNumber={latestVersion?.version}
              onVersionSelect={(version) => {
                setSearchParams({
                  ...searchParams,
                  version: String(version),
                });
              }}
            />
          ) : undefined
        }
        actionRow={[
          ...(workerId != null && isOverviewTabActive
            ? [
                <WorkerDetailTimelinePanelTrigger
                  key="versions-trigger"
                  targetDrawer={workerVersionsDrawerKey}
                  versionsCount={versions.length}
                />,
              ]
            : []),
          ...(hasWorkerRunWritePermission && workerId
            ? [
                <Button
                  key="run-worker"
                  variant="primary"
                  onClick={() => {
                    setSelectedWorkerId(workerId);
                    setRunWorkerDrawerIsOpen(true);
                  }}
                  slotLeft={
                    <Icon
                      variant="play-circle"
                      size={24}
                    />
                  }
                  size="x-small"
                >
                  Run Worker
                </Button>,
              ]
            : []),
          ...(workerActionMenuOptions.length > 0
            ? [
                <DropdownMenu
                  key="worker-more"
                  options={workerActionMenuOptions}
                >
                  <Button
                    icon="dots-three-vertical"
                    variant="outline"
                    size="small"
                  />
                </DropdownMenu>,
              ]
            : []),
        ]}
      >
        <Tab
          tabKey={WorkerDetailPageTabs.Overview}
          title="Overview"
        >
          {isViewingOldVersion ? (
            <WorkerDetailPreviousVersionBanner
              currentVersionNumber={selectedVersionData?.version}
              hasLatestVersion={latestVersion != null}
              onViewCurrent={() => {
                if (!latestVersion) return;
                setSearchParams({
                  ...searchParams,
                  version: String(latestVersion.version),
                });
              }}
            />
          ) : null}
          <Panel title="Description">
            {selectedVersionData == null ? (
              <EmptyState
                title={noVersionEmptyStateTitle}
                suggestion={noVersionEmptyStateSuggestion}
                action={
                  canCreateFirstVersion ? (
                    <Button
                      variant="primary"
                      onClick={() =>
                        navigate({
                          pathname: WorkerVersionsCreateRoutePath.toLink({
                            pathParams: { workerId },
                          }),
                        })
                      }
                    >
                      Create first version
                    </Button>
                  ) : undefined
                }
              />
            ) : selectedVersionData != null &&
              description != null &&
              description !== "" ? (
              <RichTextMarkdownDataDisplay
                value={description}
                isConstrainedToParentWidth
              />
            ) : (
              <EmptyState
                title={
                  selectedVersionData != null
                    ? "No description provided."
                    : noVersionEmptyStateTitle
                }
                suggestion={
                  selectedVersionData != null
                    ? undefined
                    : noVersionEmptyStateSuggestion
                }
              />
            )}
          </Panel>
          <WorkerVersionSchemaPanels
            version={selectedVersionData}
            noVersionEmptyStateTitle={noVersionEmptyStateTitle}
            noVersionEmptyStateSuggestion={noVersionEmptyStateSuggestion}
          />
        </Tab>
        {canViewWorkerRuns ? (
          <Tab
            tabKey={WorkerDetailPageTabs.Runs}
            title="Runs"
          >
            {workerId ? (
              <WorkerRunsTablePanel
                filterConfigStorageKey={filterConfigStorageKey}
                tableConfigStorageKey={tableConfigStorageKey}
                tableStateStorageKey={tableStateStorageKey}
                defaultFilters={{ workerId }}
              />
            ) : null}
          </Tab>
        ) : null}
      </Page>
    </>
  );
};
